import CircleIcon from "@mui/icons-material/Circle";
import { Box, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import dayjs from "dayjs";
import OutlineCard from "../../../../../../components/OutlineCard";
import Text from "../../../../../../components/Text";
import {
  ALLERGIES,
  DOSAGE_OPTIONS,
  FREQUENCY_OPTIONS,
  MEDICAL_CONDITIONS,
  SOCIAL_HISTORY_QUESTIONS,
} from "../new-intake-form-contant/intake-constant";
import { DATE_FORMAT } from "../../../../../../utils/date-time-utility";

interface PatientMedicalHistoryProps {
  values: any;
}

// Create mappers for options
const DOSAGE_MAPPER = DOSAGE_OPTIONS.reduce(
  (acc, item) => {
    acc[item.value] = item.label;
    return acc;
  },
  {} as Record<string, string>
);

const FREQUENCY_MAPPER = FREQUENCY_OPTIONS.reduce(
  (acc, item) => {
    acc[item.value] = item.label;
    return acc;
  },
  {} as Record<string, string>
);

const PatientMedicalHistory = ({ values }: PatientMedicalHistoryProps) => {
  // Format date
  const formatDate = (date: any) => {
    if (!date) return "";
    try {
      return dayjs(date).format(DATE_FORMAT);
    } catch {
      return "";
    }
  };

  // Get checked allergies
  const getCheckedAllergies = () => {
    if (!values.allergies) return [];
    return ALLERGIES.filter(
      (allergy) => values.allergies[allergy.value] === true
    ).map((allergy) => allergy.label);
  };

  // Get social history with question-answer format
  const getSocialHistoryItems = () => {
    if (!values.socialHistory) return [];
    return SOCIAL_HISTORY_QUESTIONS.map((item) => ({
      question: item.label,
      answer: values.socialHistory[item.name] === true ? "Yes" : "No",
    }));
  };

  // Get checked medical conditions
  const getCheckedMedicalConditions = () => {
    if (!values.medicalHistory) return [];
    return MEDICAL_CONDITIONS.filter(
      (condition) => values.medicalHistory[condition.value] === true
    ).map((condition) => condition.label);
  };

  // Get family history items
  const getFamilyHistoryItems = () => {
    if (!values.familyHistory || values.familyHistory.length === 0) return [];
    return values.familyHistory.filter(
      (item: any) => item.problem || item.relative || item.age
    );
  };

  // Get surgical history items
  const getSurgicalHistoryItems = () => {
    if (!values.surgicalHistory || values.surgicalHistory.length === 0)
      return [];
    return values.surgicalHistory.filter(
      (item: any) => item.type || item.date || item.doctor || item.location
    );
  };

  // Get medication items
  const getMedicationItems = () => {
    if (!values.medications || values.medications.length === 0) return [];
    return values.medications.filter(
      (item: any) =>
        item.name || item.dosage || item.frequency || item.prescribedBy
    );
  };

  // Allergies Section
  const checkedAllergies = getCheckedAllergies();
  const familyHistoryItems = getFamilyHistoryItems();
  const socialHistoryItems = getSocialHistoryItems();
  const surgicalHistoryItems = getSurgicalHistoryItems();
  const checkedMedicalConditions = getCheckedMedicalConditions();
  const medicationItems = getMedicationItems();

  return (
    <Box mt={1} id={"Patient Medical History"}>
      <Text mb={2}>PATIENT MEDICAL HISTORY</Text>

      {/* Allergies Section */}
      {(checkedAllergies.length > 0 || values.allergiesNote) && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Allergies
              </Text>
              {checkedAllergies.length > 0 && (
                <List
                  sx={{
                    "& .MuiListItem-root": {
                      alignItems: "flex-start",
                      gap: 1,
                    },
                    "& .MuiListItemIcon-root": {
                      minWidth: "auto",
                      mt: 1,
                      "& .MuiSvgIcon-root": {
                        fontSize: "8px",
                      },
                    },
                  }}
                >
                  {checkedAllergies.map((allergy, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemIcon>
                        <CircleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Text variant="body2" color="textPrimary">
                            {allergy}
                          </Text>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {values.allergiesNote && (
                <Box mt={1}>
                  <Text variant="body2" fontWeight="medium">
                    Note:
                  </Text>
                  <Text variant="body2" color="textPrimary">
                    {values.allergiesNote}
                  </Text>
                </Box>
              )}
            </Box>
          </OutlineCard>
        </Box>
      )}

      {/* Family History Section */}
      {familyHistoryItems.length > 0 && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Family History
              </Text>
              <List
                sx={{
                  "& .MuiListItem-root": {
                    alignItems: "flex-start",
                    gap: 1,
                  },
                  "& .MuiListItemIcon-root": {
                    minWidth: "auto",
                    mt: 1,
                    "& .MuiSvgIcon-root": {
                      fontSize: "8px",
                    },
                  },
                }}
              >
                {familyHistoryItems.map((item: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon>
                      <CircleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Text variant="body2" color="textPrimary">
                          {item.problem || "N/A"} - {item.relative || "N/A"}{" "}
                          (Age: {item.age || "N/A"})
                        </Text>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </OutlineCard>
        </Box>
      )}

      {/* Social History Section */}
      {(socialHistoryItems.length > 0 || values.socialHistoryNote) && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Social History
              </Text>
              {socialHistoryItems.length > 0 && (
                <List
                  sx={{
                    "& .MuiListItem-root": {
                      alignItems: "flex-start",
                      gap: 1,
                    },
                    "& .MuiListItemIcon-root": {
                      minWidth: "auto",
                      mt: 1,
                      "& .MuiSvgIcon-root": {
                        fontSize: "8px",
                      },
                    },
                  }}
                >
                  {socialHistoryItems.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemIcon>
                        <CircleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Text variant="body2" color="textPrimary">
                            {item.question} : {item.answer}
                          </Text>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {values.socialHistoryNote && (
                <Box mt={1}>
                  <Text variant="body2" fontWeight="medium">
                    Note:
                  </Text>
                  <Text variant="body2" color="textPrimary">
                    {values.socialHistoryNote}
                  </Text>
                </Box>
              )}
            </Box>
          </OutlineCard>
        </Box>
      )}

      {/* Surgical History Section */}
      {surgicalHistoryItems.length > 0 && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Surgical History
              </Text>
              <List
                sx={{
                  "& .MuiListItem-root": {
                    alignItems: "flex-start",
                    gap: 1,
                  },
                  "& .MuiListItemIcon-root": {
                    minWidth: "auto",
                    mt: 1,
                    "& .MuiSvgIcon-root": {
                      fontSize: "8px",
                    },
                  },
                }}
              >
                {surgicalHistoryItems.map((item: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon>
                      <CircleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Text variant="body2" color="textPrimary">
                          {item.type || "N/A"} -{" "}
                          {formatDate(item.date) || "N/A"} -{" "}
                          {item.doctor || "N/A"} - {item.location || "N/A"}
                        </Text>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </OutlineCard>
        </Box>
      )}

      {/* Medical History Section */}
      {(checkedMedicalConditions.length > 0 || values.medicalHistoryNote) && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Medical History
              </Text>
              {checkedMedicalConditions.length > 0 && (
                <List
                  sx={{
                    "& .MuiListItem-root": {
                      alignItems: "flex-start",
                      gap: 1,
                    },
                    "& .MuiListItemIcon-root": {
                      minWidth: "auto",
                      mt: 1,
                      "& .MuiSvgIcon-root": {
                        fontSize: "8px",
                      },
                    },
                  }}
                >
                  {checkedMedicalConditions.map((condition, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemIcon>
                        <CircleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Text variant="body2" color="textPrimary">
                            {condition}
                          </Text>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {values.medicalHistoryNote && (
                <Box mt={1}>
                  <Text variant="body2" fontWeight="medium">
                    Note:
                  </Text>
                  <Text variant="body2" color="textPrimary">
                    {values.medicalHistoryNote}
                  </Text>
                </Box>
              )}
            </Box>
          </OutlineCard>
        </Box>
      )}

      {/* Medications Section */}
      {medicationItems.length > 0 && (
        <Box mb={2}>
          <OutlineCard>
            <Box p={2}>
              <Text variant="h6" fontWeight="medium" mb={1}>
                Medications
              </Text>
              <List
                sx={{
                  "& .MuiListItem-root": {
                    alignItems: "flex-start",
                    gap: 1,
                  },
                  "& .MuiListItemIcon-root": {
                    minWidth: "auto",
                    mt: 1,
                    "& .MuiSvgIcon-root": {
                      fontSize: "8px",
                    },
                  },
                }}
              >
                {medicationItems.map((item: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon>
                      <CircleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Text variant="body2" color="textPrimary">
                          {item.name || "N/A"} -{" "}
                          {DOSAGE_MAPPER[item.dosage] || item.dosage || "N/A"} -{" "}
                          {FREQUENCY_MAPPER[item.frequency] ||
                            item.frequency ||
                            "N/A"}{" "}
                          - Prescribed by: {item.prescribedBy || "N/A"}
                        </Text>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </OutlineCard>
        </Box>
      )}
    </Box>
  );
};

export default PatientMedicalHistory;
